import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  QrCode,
  SlidersHorizontal,
  Copy,
  Plus,
  RefreshCw,
  Power,
  Trash2,
  Smartphone,
  Building2,
  Edit3,
  Lock,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Key,
  Globe,
  Check,
  ShieldCheck,
} from "lucide-react";
import {
  listQRInstances,
  createQRInstance,
  updateQRInstance,
  refreshQRCode,
  requestPairingCode,
  confirmPairing,
  logoutQRInstance,
  reconnectQRInstance,
  deleteQRInstance,
  generateInstanceToken,
  revokeInstanceToken,
  updateInstanceWebhook,
  updateInstanceAntiBanSettings,
  listTenants,
  type CurrentUser,
  type QRInstance,
  type Tenant,
} from "../../lib/api";

function WhatsAppLogo({ className = "w-5 h-5 text-white" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm.01 1.67c4.54 0 8.24 3.7 8.24 8.24 0 2.2-.86 4.28-2.42 5.83a8.18 8.18 0 0 1-5.82 2.41c-1.45 0-2.88-.39-4.14-1.13l-.3-.18-3.08.81.82-3-.2-.31a8.196 8.196 0 0 1-1.26-4.43c0-4.54 3.7-8.24 8.24-8.24zm-4.7 3.32c-.13 0-.34.05-.52.24-.18.2-.69.67-.69 1.64 0 .97.71 1.91.81 2.04.1.14 1.36 2.16 3.36 2.98 1.66.69 1.99.55 2.35.52.36-.03 1.16-.47 1.32-.93.17-.46.17-.85.12-.93-.05-.08-.18-.13-.37-.23-.19-.1-1.16-.57-1.34-.64-.18-.06-.31-.1-.44.1-.13.2-.52.64-.64.77-.12.14-.24.15-.43.05-.19-.1-.8-.29-1.52-.94-.56-.5-0.94-1.12-1.05-1.31-.11-.19-.01-.3.08-.39.09-.09.19-.23.29-.34.1-.12.13-.2.2-.34.06-.14.03-.26-.02-.36-.05-.1-.44-1.06-.6-1.45-.16-.39-.33-.33-.45-.34z" />
    </svg>
  );
}

export function QRInstancesView({ token, user, tenantId }: { token: string; user: CurrentUser; tenantId?: string }) {
  const queryClient = useQueryClient();
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";

  const [searchQuery, setSearchQuery] = useState("");
  const [connectionFilter, setConnectionFilter] = useState("all");
  const [selectedTenantFilter, setSelectedTenantFilter] = useState("all");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newInstanceName, setNewInstanceName] = useState("");
  const [newInstancePhone, setNewInstancePhone] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenantId || user.tenant_id || "");

  const [editingInstance, setEditingInstance] = useState<QRInstance | null>(null);
  const [editInstanceName, setEditInstanceName] = useState("");

  const [selectedPairingInstance, setSelectedPairingInstance] = useState<QRInstance | null>(null);
  const [pairingMethod, setPairingMethod] = useState<"qr" | "code">("qr");
  const [pairPhone, setPairPhone] = useState("");
  const [activePairCode, setActivePairCode] = useState<string | null>(null);
  const [qrCountdown, setQrCountdown] = useState(20);

  const [tokenModalInstance, setTokenModalInstance] = useState<QRInstance | null>(null);
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);
  const [webhookUrlInput, setWebhookUrlInput] = useState("");
  const [webhookSecretInput, setWebhookSecretInput] = useState("");
  const [tokenCopied, setTokenCopied] = useState(false);

  // Anti-Ban & Warm-Up State
  const [antibanModalInstance, setAntibanModalInstance] = useState<QRInstance | null>(null);
  const [warmupStage, setWarmupStage] = useState("warming_up");
  const [dailyLimit, setDailyLimit] = useState(60);
  const [simulateTyping, setSimulateTyping] = useState(true);
  const [jitterEnabled, setJitterEnabled] = useState(true);
  const [batchCooldownSize, setBatchCooldownSize] = useState(20);
  const [batchCooldownSeconds, setBatchCooldownSeconds] = useState(120);

  // Live Tenants Query (for linking instance to organization)
  const tenantsQuery = useQuery({
    queryKey: ["tenants", "list"],
    queryFn: () => listTenants(token),
  });

  const tenants = tenantsQuery.data || [];

  // Initialize selected tenant when tenants load or tenantId changes
  useEffect(() => {
    if (tenantId && tenantId !== "all") {
      setSelectedTenantId(tenantId);
    } else if (!selectedTenantId && tenants.length > 0) {
      const first = tenants[0];
      if (first) {
        setSelectedTenantId(first.id);
      }
    }
  }, [tenants, selectedTenantId, tenantId]);

  // Live QR Instances Query from Database
  const effectiveTenant = isSuperAdmin
    ? (tenantId && tenantId !== "all" ? tenantId : (selectedTenantFilter === "all" ? undefined : selectedTenantFilter))
    : user.tenant_id;

  const instancesQuery = useQuery({
    queryKey: ["qr-instances", effectiveTenant, user.tenant_id],
    queryFn: () => listQRInstances(token, effectiveTenant),
    refetchInterval: 3_000,
  });

  // Provision Instance Mutation with Tenant Linking & Dedicated Phone Number
  const createMutation = useMutation({
    mutationFn: () =>
      createQRInstance(
        token,
        {
          name: newInstanceName.trim(),
          phone_number: newInstancePhone.trim() || undefined,
        },
        selectedTenantId || user.tenant_id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setIsCreateModalOpen(false);
      setNewInstanceName("");
      setNewInstancePhone("");
      // Creating an instance only provisions the record. QR generation remains
      // an explicit operator action through the row's "Pair Device" button.
      setSelectedPairingInstance(null);
    },
    onError: (err: any) => {
      alert(`Failed to create instance: ${err.message || "Unknown error"}`);
    },
  });

  // Update Instance Mutation
  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editingInstance) throw new Error("No instance selected for editing");
      return updateQRInstance(token, editingInstance.id, {
        name: editInstanceName.trim(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setEditingInstance(null);
      setEditInstanceName("");
    },
    onError: (err: any) => {
      alert(`Failed to update instance: ${err.message || "Unknown error"}`);
    },
  });

  // Refresh QR Mutation
  const refreshQrMutation = useMutation({
    mutationFn: (instanceId: string) => refreshQRCode(token, instanceId),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setSelectedPairingInstance(updated);
      setQrCountdown(20);
    },
  });

  // Request 8-Digit Pairing Code Mutation
  const pairCodeMutation = useMutation({
    mutationFn: ({ instanceId, phone }: { instanceId: string; phone: string }) =>
      requestPairingCode(token, instanceId, phone),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setActivePairCode(data.pairing_code);
    },
  });

  // Confirm Pairing / Connect Mutation
  const confirmPairMutation = useMutation({
    mutationFn: ({ instanceId, phone }: { instanceId: string; phone: string }) =>
      confirmPairing(token, instanceId, phone, "WhatsApp User"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setSelectedPairingInstance(null);
      setActivePairCode(null);
      setPairPhone("");
    },
  });

  // Logout / Disconnect Mutation
  const logoutMutation = useMutation({
    mutationFn: (instanceId: string) => logoutQRInstance(token, instanceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["qr-instances"] }),
    onError: (err: any) => alert(err?.message || "Failed to disconnect WhatsApp instance"),
  });

  // Reconnect Mutation
  const reconnectMutation = useMutation({
    mutationFn: (instanceId: string) => reconnectQRInstance(token, instanceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["qr-instances"] }),
    onError: (err: any) => alert(err?.message || "Failed to reconnect WhatsApp instance"),
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (instanceId: string) => deleteQRInstance(token, instanceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["qr-instances"] }),
    onError: (err: any) => alert(err?.message || "Failed to delete WhatsApp instance"),
  });

  // Generate Instance API Token Mutation
  const generateTokenMut = useMutation({
    mutationFn: (instanceId: string) => generateInstanceToken(token, instanceId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setGeneratedSecret(data.token);
    },
    onError: (err: any) => alert(err?.message || "Failed to generate API token"),
  });

  // Revoke Instance API Token Mutation
  const revokeTokenMut = useMutation({
    mutationFn: (instanceId: string) => revokeInstanceToken(token, instanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setGeneratedSecret(null);
      if (tokenModalInstance) {
        setTokenModalInstance({
          ...tokenModalInstance,
          api_token_preview: undefined,
          api_token_created_at: undefined,
        });
      }
    },
    onError: (err: any) => alert(err?.message || "Failed to revoke API token"),
  });

  // Update Webhook Mutation
  const updateWebhookMut = useMutation({
    mutationFn: ({ instanceId, url, secret }: { instanceId: string; url?: string; secret?: string }) =>
      updateInstanceWebhook(token, instanceId, { webhook_url: url, webhook_secret: secret }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setTokenModalInstance(updated);
      alert("Webhook settings saved successfully");
    },
    onError: (err: any) => alert(err?.message || "Failed to update webhook settings"),
  });

  // Update Anti-Ban Settings Mutation
  const updateAntibanMutation = useMutation({
    mutationFn: () => {
      if (!antibanModalInstance) throw new Error("No instance selected");
      return updateInstanceAntiBanSettings(token, antibanModalInstance.id, {
        warmup_stage: warmupStage,
        daily_limit: Number(dailyLimit),
        simulate_typing: simulateTyping,
        jitter_enabled: jitterEnabled,
        batch_cooldown_size: Number(batchCooldownSize),
        batch_cooldown_seconds: Number(batchCooldownSeconds),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-instances"] });
      setAntibanModalInstance(null);
      alert("Anti-Ban protection & warm-up settings updated successfully!");
    },
    onError: (err: any) => alert(err?.message || "Failed to update Anti-Ban settings"),
  });


  // Live countdown timer for QR code validity
  useEffect(() => {
    if (!selectedPairingInstance || pairingMethod !== "qr") return;
    const interval = setInterval(() => {
      setQrCountdown((prev) => {
        if (prev <= 1) {
          refreshQrMutation.mutate(selectedPairingInstance.id);
          return 20;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedPairingInstance?.id, pairingMethod]);

  const instances = instancesQuery.data || [];


  // Auto-dismiss pairing modal when instance connects in background, and keep QR synchronized
  useEffect(() => {
    if (!selectedPairingInstance) return;
    const current = instances.find((i) => i.id === selectedPairingInstance.id);
    if (current) {
      if (current.status === "connected") {
        setSelectedPairingInstance(null);
        setActivePairCode(null);
      } else if (
        current.qr_code !== selectedPairingInstance.qr_code ||
        current.status !== selectedPairingInstance.status
      ) {
        setSelectedPairingInstance(current);
      }
    }
  }, [instances, selectedPairingInstance]);


  const filteredInstances = instances.filter((inst) => {
    const matchesSearch =
      inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inst.phone_number || "").includes(searchQuery) ||
      (inst.tenant_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      inst.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesConn =
      connectionFilter === "all" ||
      (connectionFilter === "connected" && inst.status === "connected") ||
      (connectionFilter === "disconnected" && inst.status !== "connected");
    const matchesTenant =
      selectedTenantFilter === "all" || inst.tenant_id === selectedTenantFilter;
    return matchesSearch && matchesConn && matchesTenant;
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header (Matches eNotify 03_instances.png) */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <QrCode className="w-6 h-6 text-emerald-600" />
            WhatsApp Instances
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage multi-device WebSocket instances linked to your organizations and tenants.
          </p>
        </div>

        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>New Instance</span>
        </button>
      </div>

      {/* Filter Toolbar (Matches eNotify 03_instances.png) */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search instances, tenants, phones..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Organization / Tenant Filter (for Super Admin & Tenant) */}
          <select
            value={selectedTenantFilter}
            onChange={(e) => setSelectedTenantFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Organization: All Tenants</option>
            {tenants.map((t: Tenant) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>

          {/* Connection Status Filter */}
          <select
            value={connectionFilter}
            onChange={(e) => setConnectionFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">Connection: All Connections</option>
            <option value="connected">Connected (Live)</option>
            <option value="disconnected">Disconnected / Pairing</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => instancesQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${instancesQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Instances Table (Exact layout with real Tenant Linking) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Organization / Tenant</th>
                <th>Instance Name</th>
                <th>Registered Phone</th>
                <th>JID Identity</th>
                <th>Status</th>
                <th>Anti-Ban Safety</th>
                <th>API Token</th>
                <th>Last Heartbeat</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {instancesQuery.isLoading ? (
                <tr>
                  <td colSpan={10} className="text-center py-16 text-slate-400 text-xs">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                      <span className="font-semibold text-slate-600">Loading WhatsApp instances...</span>
                      <span className="text-[11px] text-slate-400">Fetching live cryptographic pairing sessions and socket connections</span>
                    </div>
                  </td>
                </tr>
              ) : filteredInstances.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-400 text-xs">
                    No WhatsApp instances found. Click <strong>+ New Instance</strong> to spin up and link a pairing node.
                  </td>
                </tr>
              ) : (
                filteredInstances.map((inst: QRInstance) => (
                  <tr key={inst.id} className="hover:bg-slate-50/70 transition-colors">
                    {/* Organization / Tenant */}
                    <td>
                      <div className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        <span>{inst.tenant_name || "Primary Tenant"}</span>
                      </div>
                      <div className="font-mono text-[11px] text-slate-400 pl-5">
                        @{inst.tenant_slug || "default"}
                      </div>
                    </td>

                    {/* Instance Name + ID */}
                    <td>
                      <div className="font-bold text-slate-900 text-xs">{inst.name}</div>
                      <div className="flex items-center gap-1 text-[11px] font-mono text-slate-400">
                        <button onClick={() => copyText(inst.id)} title="Copy Instance ID" className="hover:text-slate-700">
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                        <span>{inst.id.slice(0, 12)}...</span>
                      </div>
                    </td>

                    {/* Registered Phone */}
                    <td className="font-mono text-xs font-semibold text-slate-800">
                      {inst.phone_number || "Not paired yet"}
                    </td>

                    {/* JID Identity */}
                    <td className="font-mono text-xs text-slate-500">
                      {inst.jid ? inst.jid : "—"}
                    </td>

                    {/* Status */}
                    <td>
                      <span
                        className={`status-pill ${
                          inst.status === "connected"
                            ? "status-pill--connected"
                            : inst.status === "pairing"
                            ? "status-pill--pending"
                            : "status-pill--disconnected"
                        }`}
                      >
                        {inst.status}
                      </span>
                    </td>

                    {/* Anti-Ban Safety */}
                    <td>
                      <button
                        onClick={() => {
                          setAntibanModalInstance(inst);
                          setWarmupStage(inst.anti_ban?.warmup_stage || "warming_up");
                          setDailyLimit(inst.anti_ban?.daily_limit || 60);
                          setSimulateTyping(inst.anti_ban?.simulate_typing ?? true);
                          setJitterEnabled(inst.anti_ban?.jitter_enabled ?? true);
                          setBatchCooldownSize(inst.anti_ban?.batch_cooldown_size || 20);
                          setBatchCooldownSeconds(inst.anti_ban?.batch_cooldown_seconds || 120);
                        }}
                        className="text-left group hover:opacity-90 transition-opacity"
                        title="Configure Anti-Ban Protection & Warm-Up"
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${
                              inst.anti_ban?.warmup_stage === "fresh"
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : inst.anti_ban?.warmup_stage === "warming_up"
                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                : inst.anti_ban?.warmup_stage === "established"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-purple-50 text-purple-700 border-purple-200"
                            }`}
                          >
                            {inst.anti_ban?.warmup_stage ? inst.anti_ban.warmup_stage.replace("_", " ") : "warming up"}
                          </span>
                          <ShieldCheck className="w-3 h-3 text-emerald-600 group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-16 bg-slate-100 rounded-full h-1.5 overflow-hidden border border-slate-200">
                            <div
                              className="bg-emerald-500 h-1.5 rounded-full transition-all"
                              style={{
                                width: `${Math.min(
                                  100,
                                  Math.round(
                                    ((inst.anti_ban?.sent_today || 0) /
                                      (inst.anti_ban?.daily_limit || 60)) *
                                      100
                                  )
                                )}%`,
                              }}
                            />
                          </div>
                          <span className="font-mono text-[10px] text-slate-500">
                            {inst.anti_ban?.sent_today || 0}/{inst.anti_ban?.daily_limit || 60}
                          </span>
                        </div>
                      </button>
                    </td>

                    {/* API Token */}
                    <td>
                      {inst.api_token_preview ? (
                        <button
                          onClick={() => {
                            setTokenModalInstance(inst);
                            setGeneratedSecret(null);
                            setWebhookUrlInput(inst.webhook_url || "");
                            setWebhookSecretInput(inst.webhook_secret || "");
                          }}
                          title="Manage API Token & Webhook"
                          className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors font-mono text-[11px]"
                        >
                          <Key className="w-3 h-3 text-emerald-600" />
                          <span>{inst.api_token_preview}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setTokenModalInstance(inst);
                            setGeneratedSecret(null);
                            setWebhookUrlInput(inst.webhook_url || "");
                            setWebhookSecretInput(inst.webhook_secret || "");
                          }}
                          className="text-[11px] font-medium text-slate-500 hover:text-emerald-600 flex items-center gap-1 hover:underline"
                        >
                          <Key className="w-3 h-3" />
                          <span>Create Token</span>
                        </button>
                      )}
                    </td>

                    {/* Last Heartbeat */}
                    <td className="font-mono text-xs text-slate-500">
                      {inst.last_heartbeat_at
                        ? new Date(inst.last_heartbeat_at).toLocaleTimeString()
                        : "—"}
                    </td>

                    {/* Created At */}
                    <td className="font-mono text-xs text-slate-500">
                      {new Date(inst.created_at).toLocaleDateString()}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="flex items-center gap-1.5">
                        {inst.status === "connected" ? (
                          <>
                            <button
                              onClick={() => logoutMutation.mutate(inst.id)}
                              disabled={logoutMutation.isPending}
                              title="Disconnect WhatsApp Session"
                              className="btn-danger text-xs py-1 px-2 flex items-center gap-1"
                            >
                              <Power className="w-3 h-3" />
                              <span>Disconnect</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setSelectedPairingInstance(inst);
                                const phone = inst.phone_number || "";
                                setPairPhone(phone);
                                setPairingMethod("qr");
                                setQrCountdown(20);
                                refreshQrMutation.mutate(inst.id);
                              }}
                              className="btn-primary text-xs py-1 px-2.5 flex items-center gap-1 shadow-2xs"
                            >
                              <QrCode className="w-3.5 h-3.5" />
                              <span>Pair Device</span>
                            </button>

                            <button
                              onClick={() => reconnectMutation.mutate(inst.id)}
                              disabled={reconnectMutation.isPending}
                              title="Reconnect Session"
                              className="btn-secondary text-xs py-1 px-2 flex items-center gap-1"
                            >
                              <RefreshCw className="w-3 h-3" />
                              <span>Reconnect</span>
                            </button>
                          </>
                        )}

                        <button
                          onClick={() => {
                            setAntibanModalInstance(inst);
                            setWarmupStage(inst.anti_ban?.warmup_stage || "warming_up");
                            setDailyLimit(inst.anti_ban?.daily_limit || 60);
                            setSimulateTyping(inst.anti_ban?.simulate_typing ?? true);
                            setJitterEnabled(inst.anti_ban?.jitter_enabled ?? true);
                            setBatchCooldownSize(inst.anti_ban?.batch_cooldown_size || 20);
                            setBatchCooldownSeconds(inst.anti_ban?.batch_cooldown_seconds || 120);
                          }}
                          title="Anti-Ban & Warm-Up Safety Controls"
                          className="p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                        >
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        </button>

                        <button
                          onClick={() => {
                            setTokenModalInstance(inst);
                            setGeneratedSecret(null);
                            setWebhookUrlInput(inst.webhook_url || "");
                            setWebhookSecretInput(inst.webhook_secret || "");
                          }}
                          title="Instance API Token & Webhooks"
                          className="p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                        >
                          <Key className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => {
                            setEditingInstance(inst);
                            setEditInstanceName(inst.name);
                          }}
                          title="Edit Instance Details"
                          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => {
                            if (confirm(`Delete instance "${inst.name}"?`)) {
                              deleteMutation.mutate(inst.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          title="Delete Instance"
                          className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Provision New Instance Modal with Tenant Selector */}
      {isCreateModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsCreateModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-600" />
                Create WhatsApp Instance
              </h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Spin up a real multi-device WebSocket connection slot with Signal Double Ratchet encryption and link to a tenant.
            </p>

            <div className="space-y-3.5">
              {/* Target Tenant / Organization Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Target Organization / Tenant *
                </label>
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  className="modern-input w-full text-xs font-semibold text-slate-800"
                  disabled={!isSuperAdmin && !!user.tenant_id}
                >
                  {tenants.map((t: Tenant) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slug})
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  The instance quotas and message limits will be managed under this tenant.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Instance Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. MasterG WhatsApp Node"
                  className="modern-input w-full text-xs"
                  value={newInstanceName}
                  onChange={(e) => setNewInstanceName(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Dedicated WhatsApp Contact Number (with Country Code) *
                </label>
                <input
                  type="text"
                  placeholder="e.g. 919323818985"
                  className="modern-input w-full font-mono text-xs"
                  value={newInstancePhone}
                  onChange={(e) => setNewInstancePhone(e.target.value)}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Each instance works with its own separate WhatsApp contact number and dedicated Signal Double Ratchet session keys.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setIsCreateModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createMutation.mutate()}
                  disabled={!newInstanceName.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? "Creating..." : "Create Instance"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Instance Modal */}
      {editingInstance && (
        <div className="modal-backdrop" onClick={() => setEditingInstance(null)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-emerald-600" />
                Edit WhatsApp Instance
              </h3>
              <button onClick={() => setEditingInstance(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Instance Name *
                </label>
                <input
                  type="text"
                  className="modern-input w-full text-xs"
                  value={editInstanceName}
                  onChange={(e) => setEditInstanceName(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Registered Contact Number
                </label>
                <input
                  type="text"
                  className="modern-input w-full font-mono text-xs bg-slate-50 text-slate-500"
                  value={editingInstance.phone_number || "Not paired yet"}
                  disabled
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setEditingInstance(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => updateMutation.mutate()}
                  disabled={!editInstanceName.trim() || updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Official WhatsApp Web Pairing Modal */}
      {selectedPairingInstance && (
        <div className="modal-backdrop bg-slate-900/75 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setSelectedPairingInstance(null)}>
          <div className="bg-[#EFEAE2] rounded-3xl max-w-3xl w-full shadow-2xl overflow-hidden border border-slate-300/80 my-8" onClick={(e) => e.stopPropagation()}>
            
            {/* Top Bar matching WhatsApp Green branding */}
            <div className="bg-[#008069] px-6 py-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center p-1.5">
                  <WhatsAppLogo className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-wide flex items-center gap-1.5">
                    <span>WhatsApp Web Companion Linking</span>
                    <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full font-mono">
                      {selectedPairingInstance.name}
                    </span>
                  </h3>
                  <p className="text-[11px] text-emerald-100/80 font-medium">
                    {selectedPairingInstance.tenant_name ? `Organization: ${selectedPairingInstance.tenant_name}` : "Dedicated WhatsApp Instance"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedPairingInstance(null)}
                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center text-white text-sm font-bold transition-colors"
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Top Method Tabs */}
            <div className="flex border-b border-slate-300/80 bg-[#E8E2D8] px-6 pt-3 gap-3">
              <button
                type="button"
                onClick={() => setPairingMethod("qr")}
                className={`pb-2.5 px-3 text-xs font-bold transition-all border-b-2 flex items-center gap-1.5 ${
                  pairingMethod === "qr"
                    ? "border-[#008069] text-[#008069]"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                <QrCode className="w-3.5 h-3.5" />
                <span>Scan QR Code</span>
              </button>
              <button
                type="button"
                onClick={() => setPairingMethod("code")}
                className={`pb-2.5 px-3 text-xs font-bold transition-all border-b-2 flex items-center gap-1.5 ${
                  pairingMethod === "code"
                    ? "border-[#008069] text-[#008069]"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span>8-Digit Pairing Code</span>
              </button>
            </div>

            {/* Main Content Card (Crisp White Card inside WhatsApp Web background) */}
            <div className="p-6">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {pairingMethod === "qr" ? (
                  /* Option A: Official WhatsApp QR Code UI */
                  <div className="grid grid-cols-1 md:grid-cols-12">
                    {/* Left Column: Numbered Instructions */}
                    <div className="md:col-span-7 p-7 space-y-6 flex flex-col justify-between">
                      <div>
                        <h2 className="text-2xl font-normal text-slate-800 tracking-tight mb-6">
                          Scan to log in
                        </h2>

                        <ol className="space-y-3.5 text-sm text-slate-600">
                          <li className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full border border-slate-300 text-slate-600 flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
                              1
                            </span>
                            <span>Open <strong className="text-slate-800">WhatsApp</strong> on your phone</span>
                          </li>

                          <li className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full border border-slate-300 text-slate-600 flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
                              2
                            </span>
                            <span>Tap <strong className="text-slate-800">Menu (⋮)</strong> on Android or <strong className="text-slate-800">Settings (⚙️)</strong> on iPhone</span>
                          </li>

                          <li className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full border border-slate-300 text-slate-600 flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
                              3
                            </span>
                            <span>Tap <strong className="text-slate-800">Linked Devices</strong> and then tap <strong className="text-slate-800">Link a Device</strong></span>
                          </li>

                          <li className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full border border-slate-300 text-slate-600 flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
                              4
                            </span>
                            <span>Point your phone at this screen to capture the QR code</span>
                          </li>
                        </ol>

                        <div className="mt-4">
                          <a
                            href="https://faq.whatsapp.com/1317564962315842"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#008069] text-xs font-medium hover:underline inline-flex items-center gap-1"
                          >
                            <span>Need help?</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                        <label className="flex items-center gap-2 text-slate-600 select-none cursor-pointer">
                          <input type="checkbox" defaultChecked className="rounded text-[#008069] focus:ring-[#008069]" />
                          <span>Stay logged in on this browser</span>
                        </label>

                        <button
                          type="button"
                          onClick={() => setPairingMethod("code")}
                          className="text-[#008069] font-bold hover:underline flex items-center gap-1 text-xs"
                        >
                          <span>Log in with phone number</span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Right Column: Clean, Unobstructed QR Code Matrix */}
                    <div className="md:col-span-5 p-7 bg-slate-50/70 border-t md:border-t-0 md:border-l border-slate-100 flex flex-col items-center justify-center text-center space-y-3">
                      <div className="w-[230px] h-[230px] p-3.5 bg-white rounded-2xl shadow-sm border border-slate-200 flex items-center justify-center">
                        {selectedPairingInstance.qr_code ? (
                          <QRCodeSVG
                            value={selectedPairingInstance.qr_code}
                            size={200}
                            level="M"
                            fgColor="#111B21"
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-slate-500">
                            <RefreshCw className="w-7 h-7 text-[#008069] animate-spin" />
                            <span className="text-xs font-medium">Generating secure QR…</span>
                          </div>
                        )}
                      </div>

                      {/* Auto-Refresh Indicator */}
                      <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                        <RefreshCw className="w-3 h-3 text-[#008069] animate-spin" />
                        <span>Auto-refreshing in <strong className="text-slate-800 font-mono">{qrCountdown}s</strong></span>
                      </div>

                      {/* QR Action Buttons */}
                      <div className="w-full pt-1 flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => refreshQrMutation.mutate(selectedPairingInstance.id)}
                          disabled={refreshQrMutation.isPending}
                          className="py-1.5 px-3 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-medium flex items-center gap-1.5 transition-colors"
                        >
                          <RefreshCw className={`w-3 h-3 text-[#008069] ${refreshQrMutation.isPending ? "animate-spin" : ""}`} />
                          <span>{refreshQrMutation.isPending ? "Refreshing..." : "Refresh QR"}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPairingMethod("code")}
                          className="py-1.5 px-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-[#008069] text-xs font-semibold flex items-center gap-1 transition-colors"
                        >
                          <Smartphone className="w-3 h-3" />
                          <span>Use Phone Code</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Option B: Official Phone Number Pairing UI ("Log in with phone number") */
                  <div className="p-8 space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                      <div>
                        <h2 className="text-2xl font-normal text-slate-800 tracking-tight">
                          Enter phone number
                        </h2>
                        <p className="text-xs text-slate-500 mt-1">
                          Select country and enter your WhatsApp phone number to pair companion device.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setPairingMethod("qr")}
                        className="text-[#008069] font-bold hover:underline flex items-center gap-1 text-xs"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        <span>Log in with QR code</span>
                      </button>
                    </div>

                    <div className="max-w-md mx-auto space-y-4">
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          WhatsApp Phone Number (with Country Code)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="e.g. 919323818985"
                            value={pairPhone}
                            onChange={(e) => setPairPhone(e.target.value)}
                            className="modern-input flex-1 text-sm font-mono py-2"
                          />
                          <button
                            onClick={() =>
                              pairCodeMutation.mutate({
                                instanceId: selectedPairingInstance.id,
                                phone: pairPhone,
                              })
                            }
                            disabled={!pairPhone.trim() || pairCodeMutation.isPending}
                            className="py-2 px-4 rounded-lg bg-[#008069] hover:bg-[#00705c] text-white text-xs font-semibold shadow-xs transition-colors whitespace-nowrap"
                          >
                            {pairCodeMutation.isPending ? "Generating..." : "Next / Get Code"}
                          </button>
                        </div>
                      </div>

                      {activePairCode && (
                        <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 text-center space-y-4">
                          <span className="text-xs text-slate-600 font-medium">
                            Enter this 8-digit code on WhatsApp:
                          </span>

                          {/* 8-Digit Split Boxes (Official WhatsApp Web format) */}
                          <div className="flex items-center justify-center gap-2">
                            {/* First 4 Characters */}
                            <div className="flex gap-1.5">
                              {activePairCode.replace("-", "").slice(0, 4).split("").map((c, i) => (
                                <div
                                  key={i}
                                  className="w-10 h-12 rounded-lg bg-white border-2 border-[#008069] text-[#008069] flex items-center justify-center font-mono text-xl font-extrabold shadow-2xs"
                                >
                                  {c}
                                </div>
                              ))}
                            </div>

                            <span className="text-slate-400 font-bold text-lg px-1">—</span>

                            {/* Last 4 Characters */}
                            <div className="flex gap-1.5">
                              {activePairCode.replace("-", "").slice(4, 8).split("").map((c, i) => (
                                <div
                                  key={i}
                                  className="w-10 h-12 rounded-lg bg-white border-2 border-[#008069] text-[#008069] flex items-center justify-center font-mono text-xl font-extrabold shadow-2xs"
                                >
                                  {c}
                                </div>
                              ))}
                            </div>

                            <button
                              onClick={() => copyText(activePairCode)}
                              title="Copy Pairing Code"
                              className="p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-600 transition-colors ml-1"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Mobile Screen Guidance */}
                          <div className="p-3.5 bg-emerald-50 rounded-xl border border-emerald-200/80 text-left text-xs text-slate-700 space-y-1">
                            <p className="font-bold text-emerald-950 flex items-center gap-1.5">
                              <Smartphone className="w-4 h-4 text-[#008069]" />
                              On your mobile phone:
                            </p>
                            <p className="text-[11px] text-slate-600">
                              Tap <strong>"Link with phone number instead"</strong> at the bottom of the camera screen, and type the 8-digit code shown above.
                            </p>
                          </div>

                          <button
                            onClick={() =>
                              confirmPairMutation.mutate({
                                instanceId: selectedPairingInstance.id,
                                phone: pairPhone || "919323818985",
                              })
                            }
                            disabled={confirmPairMutation.isPending}
                            className="w-full py-2.5 px-4 rounded-xl bg-[#008069] hover:bg-[#00705c] text-white text-xs font-bold shadow-xs transition-colors"
                          >
                            {confirmPairMutation.isPending ? "Validating Session..." : "Confirm Paired On Phone"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer with E2EE Badge */}
            <div className="px-6 py-3 bg-[#EFEAE2] flex items-center justify-center gap-1.5 text-[11px] text-slate-500 font-medium">
              <Lock className="w-3.5 h-3.5 text-slate-400" />
              <span>Your personal messages and companion socket are end-to-end encrypted</span>
            </div>

          </div>
        </div>
      )}

      {/* Instance API Token & Webhooks Modal */}
      {tokenModalInstance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/80">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-[#008069]/10 flex items-center justify-center text-[#008069]">
                  <Key className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <span>Instance API Token & Webhooks</span>
                    <span className="text-[11px] font-normal text-slate-500">
                      ({tokenModalInstance.name})
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Authenticate external systems directly to this WhatsApp instance's inbox
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setTokenModalInstance(null);
                  setGeneratedSecret(null);
                  setTokenCopied(false);
                }}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Newly Generated Secret Banner */}
              {generatedSecret ? (
                <div className="p-4 rounded-xl bg-emerald-50 border-2 border-emerald-500/50 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-900 font-bold text-xs">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span>New Instance API Token Generated!</span>
                  </div>
                  <p className="text-[11px] text-emerald-800">
                    Copy and store this token securely now. For security, it will <strong>never be shown again</strong>.
                  </p>
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="text"
                      readOnly
                      value={generatedSecret}
                      className="flex-1 font-mono text-xs bg-white border border-emerald-300 rounded-lg px-3 py-2 text-emerald-900 select-all"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedSecret);
                        setTokenCopied(true);
                        setTimeout(() => setTokenCopied(false), 2000);
                      }}
                      className="px-3.5 py-2 rounded-lg bg-[#008069] text-white text-xs font-bold hover:bg-[#00705c] transition-colors flex items-center gap-1.5 shadow-xs"
                    >
                      {tokenCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{tokenCopied ? "Copied" : "Copy"}</span>
                    </button>
                  </div>
                </div>
              ) : tokenModalInstance.api_token ? (
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-bold text-slate-800">Active API Token</div>
                      <div className="text-[11px] text-slate-500 pt-0.5">
                        Use this in <span className="font-mono font-bold text-slate-700">X-Instance-Token</span> header for external REST APIs.
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                      Active
                    </span>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="text"
                      readOnly
                      value={tokenModalInstance.api_token}
                      className="flex-1 font-mono text-xs bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 select-all"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(tokenModalInstance.api_token || "");
                        setTokenCopied(true);
                        setTimeout(() => setTokenCopied(false), 2000);
                      }}
                      className="px-3.5 py-2 rounded-lg bg-[#008069] text-white text-xs font-bold hover:bg-[#00705c] transition-colors flex items-center gap-1.5 shadow-xs whitespace-nowrap"
                    >
                      {tokenCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{tokenCopied ? "Copied" : "Copy Token"}</span>
                    </button>
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                    <button
                      onClick={() => {
                        if (confirm("Regenerate token? The previous token will immediately stop working.")) {
                          generateTokenMut.mutate(tokenModalInstance.id);
                        }
                      }}
                      disabled={generateTokenMut.isPending}
                      className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${generateTokenMut.isPending ? "animate-spin" : ""}`} />
                      <span>Regenerate Token</span>
                    </button>

                    <button
                      onClick={() => {
                        if (confirm("Revoke this token? External requests using it will be rejected.")) {
                          revokeTokenMut.mutate(tokenModalInstance.id);
                        }
                      }}
                      disabled={revokeTokenMut.isPending}
                      className="btn-danger text-xs py-1.5 px-3 flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Revoke Token</span>
                    </button>

                    <Link
                      to={`/admin/qr-api-test?instance_id=${tokenModalInstance.id}`}
                      className="ml-auto text-xs font-bold text-[#008069] hover:underline flex items-center gap-1"
                    >
                      <span>Open in Inbox API Lab</span>
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              ) : tokenModalInstance.api_token_preview ? (
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-bold text-slate-800">Active API Token</div>
                      <div className="text-[11px] font-mono text-slate-500 pt-0.5">
                        Prefix: <span className="font-semibold text-slate-700">{tokenModalInstance.api_token_preview}</span>
                        {tokenModalInstance.api_token_created_at && (
                          <span className="ml-2 text-slate-400">
                            (created {new Date(tokenModalInstance.api_token_created_at).toLocaleDateString()})
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                      Active
                    </span>
                  </div>

                  <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
                    <button
                      onClick={() => {
                        if (confirm("Regenerate token? The previous token will immediately stop working.")) {
                          generateTokenMut.mutate(tokenModalInstance.id);
                        }
                      }}
                      disabled={generateTokenMut.isPending}
                      className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${generateTokenMut.isPending ? "animate-spin" : ""}`} />
                      <span>Regenerate Token</span>
                    </button>

                    <button
                      onClick={() => {
                        if (confirm("Revoke this token? External requests using it will be rejected.")) {
                          revokeTokenMut.mutate(tokenModalInstance.id);
                        }
                      }}
                      disabled={revokeTokenMut.isPending}
                      className="btn-danger text-xs py-1.5 px-3 flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Revoke Token</span>
                    </button>

                    <Link
                      to={`/admin/qr-api-test?instance_id=${tokenModalInstance.id}`}
                      className="ml-auto text-xs font-bold text-[#008069] hover:underline flex items-center gap-1"
                    >
                      <span>Open in Inbox API Lab</span>
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200 space-y-3">
                  <div className="text-xs font-bold text-amber-900">No API Token Assigned</div>
                  <p className="text-[11px] text-amber-800">
                    Generate an API token to allow external servers, CRM systems, and bots to send messages and read chats via this WhatsApp instance.
                  </p>
                  <button
                    onClick={() => generateTokenMut.mutate(tokenModalInstance.id)}
                    disabled={generateTokenMut.isPending}
                    className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
                  >
                    <Key className="w-3.5 h-3.5" />
                    <span>{generateTokenMut.isPending ? "Generating..." : "Generate Instance API Token"}</span>
                  </button>
                </div>
              )}

              {/* Webhook Configuration Section */}
              <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-bold text-slate-900">Instance Inbound Webhook</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  WA Master delivers realtime HTTP POST notifications for inbound customer messages, status acks, and reactions.
                </p>

                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      Webhook Target URL
                    </label>
                    <input
                      type="url"
                      placeholder="https://your-domain.com/webhooks/whatsapp"
                      value={webhookUrlInput}
                      onChange={(e) => setWebhookUrlInput(e.target.value)}
                      className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:border-[#008069]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      Webhook Secret (Optional HMAC verification)
                    </label>
                    <input
                      type="text"
                      placeholder="whsec_xxxxxxxxxxxxxxxxx"
                      value={webhookSecretInput}
                      onChange={(e) => setWebhookSecretInput(e.target.value)}
                      className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:border-[#008069]"
                    />
                  </div>
                  <div className="pt-2 flex justify-end">
                    <button
                      onClick={() =>
                        updateWebhookMut.mutate({
                          instanceId: tokenModalInstance.id,
                          url: webhookUrlInput,
                          secret: webhookSecretInput,
                        })
                      }
                      disabled={updateWebhookMut.isPending}
                      className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow-xs transition-colors"
                    >
                      {updateWebhookMut.isPending ? "Saving..." : "Save Webhook Settings"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
              <span>Instance ID: <span className="font-mono text-slate-700">{tokenModalInstance.id}</span></span>
              <button
                onClick={() => {
                  setTokenModalInstance(null);
                  setGeneratedSecret(null);
                }}
                className="text-xs font-bold text-slate-600 hover:text-slate-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Anti-Ban & Warm-Up Safety Modal */}
      {antibanModalInstance && (
        <div className="modal-backdrop" onClick={() => setAntibanModalInstance(null)}>
          <div className="modal-card max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Anti-Ban Protection & Warm-Up Safety</h3>
                  <p className="text-[11px] text-slate-500">
                    Active safeguards for {antibanModalInstance.name} ({antibanModalInstance.phone_number || "Unpaired"})
                  </p>
                </div>
              </div>
              <button onClick={() => setAntibanModalInstance(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1 text-xs">
              {/* Warm-up Stage Presets */}
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-2.5">
                <label className="block text-xs font-bold text-slate-800">
                  Instance Warm-Up Stage & Recommended Daily Quota
                </label>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  WhatsApp tracks sudden dispatch spikes on new accounts. Pick a tier matching your SIM registration age to prevent algorithmic account bans.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                  {[
                    { id: "fresh", label: "Stage 1: Fresh", limit: 25, desc: "0-7 days old SIM" },
                    { id: "warming_up", label: "Stage 2: Warm-Up", limit: 60, desc: "1-2 weeks active" },
                    { id: "established", label: "Stage 3: Established", limit: 250, desc: "1+ month history" },
                    { id: "mature", label: "Stage 4: Mature", limit: 1000, desc: "High reputation" },
                  ].map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => {
                        setWarmupStage(preset.id);
                        setDailyLimit(preset.limit);
                      }}
                      className={`p-2.5 rounded-lg border text-left transition-all ${
                        warmupStage === preset.id
                          ? "bg-emerald-50 border-emerald-500 text-emerald-950 ring-1 ring-emerald-500 shadow-2xs"
                          : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <div className="font-bold text-[11px]">{preset.label}</div>
                      <div className="text-[10px] text-emerald-700 font-semibold">{preset.limit} msgs/day</div>
                      <div className="text-[9px] text-slate-400 mt-0.5">{preset.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Daily Cap & Today's Metric */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border border-slate-200 bg-white">
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Daily Message Cap
                  </label>
                  <input
                    type="number"
                    min={10}
                    max={5000}
                    value={dailyLimit}
                    onChange={(e) => setDailyLimit(Math.max(1, parseInt(e.target.value) || 0))}
                    className="w-full text-xs font-mono px-3 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:border-emerald-600"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">Maximum allowed outbound sends per 24 hours</span>
                </div>

                <div className="p-3 rounded-lg border border-slate-200 bg-emerald-50/50">
                  <div className="text-[11px] font-bold text-emerald-900 mb-1">
                    Today's Dispatch Status
                  </div>
                  <div className="font-mono text-sm font-bold text-emerald-800">
                    {antibanModalInstance.anti_ban?.sent_today || 0} / {antibanModalInstance.anti_ban?.daily_limit || dailyLimit}
                  </div>
                  <div className="text-[10px] text-emerald-700 mt-1">
                    Remaining today: {Math.max(0, (antibanModalInstance.anti_ban?.daily_limit || dailyLimit) - (antibanModalInstance.anti_ban?.sent_today || 0))} messages
                  </div>
                </div>
              </div>

              {/* Behavioral Protections */}
              <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-3">
                <div className="font-bold text-xs text-slate-900">Behavioral Anti-Detection Controls</div>

                {/* Simulate Human Typing */}
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={simulateTyping}
                    onChange={(e) => setSimulateTyping(e.target.checked)}
                    className="mt-0.5 rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                  />
                  <div>
                    <span className="font-semibold text-xs text-slate-800">Emulate Human Typing Presence</span>
                    <p className="text-[11px] text-slate-500">
                      Sends real WhatsApp composing chatstate packets (typing indicator) for 1.5 - 3.5s before dispatching messages. Eliminates instant bot-like delivery signatures.
                    </p>
                  </div>
                </label>

                {/* Randomized Dispatch Jitter */}
                <label className="flex items-start gap-2.5 cursor-pointer pt-2 border-t border-slate-100">
                  <input
                    type="checkbox"
                    checked={jitterEnabled}
                    onChange={(e) => setJitterEnabled(e.target.checked)}
                    className="mt-0.5 rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                  />
                  <div>
                    <span className="font-semibold text-xs text-slate-800">Randomized Interval Jitter</span>
                    <p className="text-[11px] text-slate-500">
                      Injects 1.5s to 4.5s random jitter between outbound campaign dispatches to defeat rhythmic spam detection heuristics.
                    </p>
                  </div>
                </label>
              </div>

              {/* Micro-Batching & Resting Break */}
              <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-3">
                <div className="font-bold text-xs text-slate-900">Micro-Batch Cooldown Breaks</div>
                <p className="text-[11px] text-slate-500">
                  Pauses large campaign queues periodically to emulate natural human pauses and let incoming replies settle.
                </p>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      Batch Size (Messages)
                    </label>
                    <input
                      type="number"
                      min={5}
                      max={100}
                      value={batchCooldownSize}
                      onChange={(e) => setBatchCooldownSize(Math.max(5, parseInt(e.target.value) || 20))}
                      className="w-full text-xs font-mono px-3 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:border-emerald-600"
                    />
                    <span className="text-[10px] text-slate-400">Rest after this many messages</span>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      Rest Duration (Seconds)
                    </label>
                    <input
                      type="number"
                      min={30}
                      max={600}
                      value={batchCooldownSeconds}
                      onChange={(e) => setBatchCooldownSeconds(Math.max(10, parseInt(e.target.value) || 120))}
                      className="w-full text-xs font-mono px-3 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:border-emerald-600"
                    />
                    <span className="text-[10px] text-slate-400">Pause duration before next batch</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 mt-4 border-t border-slate-200 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">
                Changes apply immediately to all outbound campaigns and inbox dispatches.
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setAntibanModalInstance(null)}
                  className="btn-secondary text-xs py-1.5 px-3"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => updateAntibanMutation.mutate()}
                  disabled={updateAntibanMutation.isPending}
                  className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5 shadow-2xs"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>{updateAntibanMutation.isPending ? "Saving..." : "Save Anti-Ban Settings"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
